import React, { useEffect, useState } from 'react';
import { Badge, Button, ButtonGroup, Col, Form, Row, Table } from 'react-bootstrap';
import { ResourceField, ResourceFormPage } from '../../components/resource/ResourceFormPage';
import { SituacaoProducaoDiaTrabalho, DiaTrabalho, ProducaoDiaTrabalho } from '../../types/producao';
import { diaTrabalhoService, fichaTecnicaService, operadoresService } from '../../services';
import { makeOptionLoader } from '../../utils/optionLoaders';

const responsavelOptionLoader = makeOptionLoader(
  operadoresService.list,
  (item) => item.nome ?? String(item.id),
);

const fields: ResourceField<DiaTrabalho>[] = [
  {
    name: 'data',
    label: 'Data do dia',
    type: 'date',
    required: true,
  },
  {
    name: 'responsavel',
    label: 'Responsável',
    type: 'async-select',
    valueType: 'number',
    required: true,
    loadOptions: responsavelOptionLoader,
    showSearchControl: false,
  },
  {
    name: 'producoes',
    label: 'Produções',
    hidden: true,
  },
];

const situacaoLabels: Record<SituacaoProducaoDiaTrabalho, string> = {
  ABERTO: 'Aberto',
  PRODUZIDO: 'Produzido',
  CANCELADO: 'Cancelado',
};

const situacaoBadge: Record<SituacaoProducaoDiaTrabalho, string> = {
  ABERTO: 'warning',
  PRODUZIDO: 'success',
  CANCELADO: 'secondary',
};

type SelectOption = { value: number; label: string };

interface ProducaoDraft {
  ficha_tecnica?: number;
  quantidade: string;
  produzido: string;
  situacao: SituacaoProducaoDiaTrabalho;
}

const buildDraft = (overrides?: Partial<ProducaoDraft>): ProducaoDraft => ({
  ficha_tecnica: overrides?.ficha_tecnica,
  quantidade: overrides?.quantidade ?? '',
  produzido: overrides?.produzido ?? '',
  situacao: overrides?.situacao ?? 'ABERTO',
});

const parseDecimalInput = (value: string): number | null => {
  if (!value) return null;
  const normalized = value.replace(/\s+/g, '').replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value?: number): string =>
  value != null
    ? new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 }).format(value)
    : '0';

const ProducoesManager: React.FC<{
  formData: Record<string, unknown>;
  setFieldValue: (name: string, value: unknown) => void;
}> = ({ formData, setFieldValue }) => {
  const producoes = Array.isArray(formData.producoes)
    ? (formData.producoes as ProducaoDiaTrabalho[])
    : [];
  const [draft, setDraft] = useState<ProducaoDraft>(() => buildDraft());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [fichaOptions, setFichaOptions] = useState<SelectOption[]>([]);
  const [loadingFichas, setLoadingFichas] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoadingFichas(true);
    fichaTecnicaService
      .list({ page_size: 200 })
      .then((response) => {
        if (!mounted) return;
        const options = (response.results ?? [])
          .map((ficha) => ({
            value: ficha.id ?? 0,
            label: `${ficha.codigo ?? ''} ${ficha.titulo ?? ''}`.trim() || String(ficha.id),
          }))
          .filter((option) => option.value !== 0);
        setFichaOptions(options);
      })
      .catch(() => {
        if (mounted) {
          setFichaOptions([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoadingFichas(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const setProducoes = (next: ProducaoDiaTrabalho[]) => {
    setFieldValue('producoes', next);
  };

  const resetDraft = () => {
    setDraft(buildDraft());
    setEditingIndex(null);
  };

  const handleSave = () => {
    const quantidade = parseDecimalInput(draft.quantidade);
    if (!draft.ficha_tecnica || quantidade === null) {
      return;
    }
    const produzido = parseDecimalInput(draft.produzido) ?? 0;
    const baseEntry: ProducaoDiaTrabalho = {
      ficha_tecnica: draft.ficha_tecnica,
      quantidade,
      produzido,
      situacao: draft.situacao,
      id: editingIndex != null ? producoes[editingIndex]?.id : undefined,
    } as ProducaoDiaTrabalho;
    const nextProducoes = editingIndex != null
      ? producoes.map((entry, idx) => (idx === editingIndex ? { ...entry, ...baseEntry } : entry))
      : [...producoes, baseEntry];
    setProducoes(nextProducoes);
    resetDraft();
  };

  const handleEdit = (index: number) => {
    const entry = producoes[index];
    if (!entry) return;
    setDraft(
      buildDraft({
        ficha_tecnica: entry.ficha_tecnica,
        quantidade: entry.quantidade != null ? String(entry.quantidade) : '',
        produzido: entry.produzido != null ? String(entry.produzido) : '',
        situacao: entry.situacao,
      }),
    );
    setEditingIndex(index);
  };

  const handleRemove = (index: number) => {
    setProducoes(producoes.filter((_, idx) => idx !== index));
    if (editingIndex === index) {
      resetDraft();
    }
  };

  const hasEntries = producoes.length > 0;

  return (
    <div className="border rounded-3 p-4 bg-white">
      <h6 className="mb-3">Produções do dia</h6>
      <Row className="g-3 align-items-end">
        <Col xs={12} md={4}>
          <Form.Group controlId="dia-trabalho-ficha">
            <Form.Label>Ficha técnica</Form.Label>
            <Form.Select
              value={draft.ficha_tecnica ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  ficha_tecnica: event.target.value ? Number(event.target.value) : undefined,
                }))
              }
              disabled={loadingFichas}
            >
              <option value="">Selecione</option>
              {fichaOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={2}>
          <Form.Group controlId="dia-trabalho-quantidade">
            <Form.Label>Quantidade prevista</Form.Label>
            <Form.Control
              type="number"
              step="0.001"
              value={draft.quantidade}
              onChange={(event) => setDraft((prev) => ({ ...prev, quantidade: event.target.value }))}
              placeholder="0,000"
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={2}>
          <Form.Group controlId="dia-trabalho-produzido">
            <Form.Label>Produzido</Form.Label>
            <Form.Control
              type="number"
              step="0.001"
              value={draft.produzido}
              onChange={(event) => setDraft((prev) => ({ ...prev, produzido: event.target.value }))}
              placeholder="0,000"
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={2}>
          <Form.Group controlId="dia-trabalho-situacao">
            <Form.Label>Situação</Form.Label>
            <Form.Select
              value={draft.situacao}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  situacao: event.target.value as SituacaoProducaoDiaTrabalho,
                }))
              }
            >
              {Object.entries(situacaoLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={2} className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={handleSave}
            className="flex-grow-1"
            disabled={!draft.ficha_tecnica || parseDecimalInput(draft.quantidade) === null}
          >
            {editingIndex != null ? 'Atualizar' : 'Adicionar'}
          </Button>
          <Button variant="outline-secondary" onClick={resetDraft}>
            Limpar
          </Button>
        </Col>
      </Row>

      {hasEntries ? (
        <Table size="sm" className="mt-4" responsive>
          <thead>
            <tr>
              <th>Ficha técnica</th>
              <th>Prevista</th>
              <th>Produzido</th>
              <th>Situação</th>
              <th className="text-end">Ações</th>
            </tr>
          </thead>
          <tbody>
            {producoes.map((entry, index) => (
              <tr key={entry.id ?? index}>
                <td>{entry.ficha_tecnica_codigo ?? `#${entry.ficha_tecnica}`}</td>
                <td>{formatNumber(entry.quantidade)}</td>
                <td>{formatNumber(entry.produzido)}</td>
                <td>
                  <Badge bg={situacaoBadge[entry.situacao] ?? 'secondary'}>
                    {situacaoLabels[entry.situacao] ?? entry.situacao}
                  </Badge>
                </td>
                <td className="text-end">
                  <ButtonGroup size="sm">
                    <Button variant="outline-secondary" onClick={() => handleEdit(index)}>
                      Editar
                    </Button>
                    <Button variant="outline-danger" onClick={() => handleRemove(index)}>
                      Remover
                    </Button>
                  </ButtonGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted small mt-3 mb-0">Nenhuma produção registrada para o dia.</p>
      )}
    </div>
  );
};

export const DiaTrabalhoFormPage: React.FC = () => (
  <section className="admin-shell__content">
    <div className="py-4 px-4 container-fluid">
      <ResourceFormPage
        title="Dia de trabalho"
        description="Registre as produções do dia e os responsáveis pelas fichas técnicas."
        fields={fields}
        service={diaTrabalhoService}
        redirectPath="/admin/producao/dias-trabalho"
        cancelPath="/admin/producao/dias-trabalho"
        initialValues={{ producoes: [] }}
        renderAfterActions={(formData, setFieldValue) => (
          <ProducoesManager formData={formData} setFieldValue={setFieldValue} />
        )}
      />
    </div>
  </section>
);
